import React from "react";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { fetchDocksForLocationRef, fetchWarehouseSlotsAvailability } from "../TabContent/api/AppointmentsApi";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const ClosureWarningModal = ({
  appointments = [],
  warehouses = [],
  defaultWarehouseRef = "",
  isLoading = false,
  isSubmitting = false,
  onCancel,
  onProceed,
  onReschedule,
  title = "Appointments in Selected Range",
}) => {
  const [rowStateById, setRowStateById] = React.useState({});
  const [reschedulingId, setReschedulingId] = React.useState(null);

  const buildRowState = React.useCallback(
    (appt) => ({
      expanded: false,
      warehouseRef: appt.warehouse || defaultWarehouseRef || "",
      dockId: appt.dockNumber ? String(appt.dockNumber) : "",
      date: appt.startTime ? appt.startTime.split("T")[0] : "",
      docks: [],
      slots: [],
      selectedSlot: null,
      isDocksLoading: false,
      isSlotsLoading: false,
      dockError: null,
      slotError: null,
    }),
    [defaultWarehouseRef]
  );

  React.useEffect(() => {
    const activeIds = new Set((appointments || []).map((appt) => appt.appointmentId));
    setRowStateById((prev) => {
      const next = {};
      (appointments || []).forEach((appt) => {
        const id = appt.appointmentId;
        if (!id) return;
        next[id] = prev[id] ? { ...prev[id] } : buildRowState(appt);
      });
      return next;
    });
  }, [appointments, buildRowState]);

  const updateRow = (id, patch) => {
    setRowStateById((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        ...patch,
      },
    }));
  };

  const loadDocksForRow = async (appt, warehouseOverride, options = {}) => {
    const id = appt.appointmentId;
    const row = rowStateById[id] || buildRowState(appt);
    const { force = false, preferredDockId } = options;
    const warehouseRef = warehouseOverride || row.warehouseRef;
    if (!warehouseRef) return;
    if (!force && row.docks.length > 0) return row.dockId;

    updateRow(id, { isDocksLoading: true, dockError: null });
    try {
      const docks = await fetchDocksForLocationRef(warehouseRef);
      const dockList = Array.isArray(docks) ? docks : [];
      const priorDockId =
        preferredDockId !== undefined ? preferredDockId : row.dockId;
      const nextDockId =
        priorDockId ||
        (dockList[0] ? String(dockList[0].dockId || dockList[0].dockNumber) : "");
      updateRow(id, {
        docks: dockList,
        dockId: nextDockId,
        isDocksLoading: false,
      });
      return nextDockId;
    } catch (err) {
      updateRow(id, { isDocksLoading: false, dockError: "Failed to load docks." });
      return null;
    }
  };

  const loadSlotsForRow = async (appt, overrides = {}) => {
    const id = appt.appointmentId;
    const row = rowStateById[id] || buildRowState(appt);
    const warehouseRef = overrides.warehouseRef || row.warehouseRef;
    const date = overrides.date || row.date;
    const dockId = overrides.dockId || row.dockId;
    if (!warehouseRef || !date || !dockId) return;

    updateRow(id, { isSlotsLoading: true, slotError: null });
    try {
      const data = await fetchWarehouseSlotsAvailability(warehouseRef, date);
      const match = (data || []).find(
        (item) => String(item.dockId) === String(dockId)
      );
      const slots = Array.isArray(match?.slots) ? match.slots : [];
      const selectedSlot =
        row.selectedSlot && slots.some((s) => s.startTime === row.selectedSlot.startTime)
          ? row.selectedSlot
          : null;
      updateRow(id, { slots, selectedSlot, isSlotsLoading: false });
    } catch (err) {
      updateRow(id, { isSlotsLoading: false, slotError: "Failed to load slots." });
    }
  };

  const handleToggleRow = async (appt) => {
    const id = appt.appointmentId;
    const row = rowStateById[id] || buildRowState(appt);
    const expanded = !row.expanded;
    updateRow(id, { expanded });
    if (expanded) {
      const nextDockId = await loadDocksForRow(appt);
      if (nextDockId) {
        await loadSlotsForRow(appt, { dockId: nextDockId });
      } else {
        await loadSlotsForRow(appt);
      }
    }
  };

  const handleWarehouseChange = async (appt, warehouseRef) => {
    const id = appt.appointmentId;
    updateRow(id, {
      warehouseRef,
      dockId: "",
      docks: [],
      slots: [],
      selectedSlot: null,
    });
    const nextDockId = await loadDocksForRow(appt, warehouseRef, {
      force: true,
      preferredDockId: "",
    });
    if (nextDockId) {
      await loadSlotsForRow(appt, { warehouseRef, dockId: nextDockId });
    }
  };

  const handleDockChange = async (appt, dockId) => {
    const id = appt.appointmentId;
    updateRow(id, { dockId, slots: [], selectedSlot: null });
    await loadSlotsForRow(appt, {
      dockId,
      warehouseRef: rowStateById[id]?.warehouseRef,
      date: rowStateById[id]?.date,
    });
  };

  const handleDateChange = async (appt, date) => {
    const id = appt.appointmentId;
    updateRow(id, { date, slots: [], selectedSlot: null });
    await loadSlotsForRow(appt, {
      date,
      warehouseRef: rowStateById[id]?.warehouseRef,
      dockId: rowStateById[id]?.dockId,
    });
  };

  const handleSlotChange = (id, slot) => {
    updateRow(id, { selectedSlot: slot });
  };

  const handleReschedule = async (appt) => {
    if (!onReschedule) return;
    const id = appt.appointmentId;
    const row = rowStateById[id] || buildRowState(appt);
    if (!row.selectedSlot || !row.warehouseRef || !row.dockId || !row.date) return;
    setReschedulingId(id);
    try {
      await onReschedule(appt, {
        warehouseRef: row.warehouseRef,
        dockId: row.dockId,
        date: row.date,
        slot: row.selectedSlot,
      });
      updateRow(id, { selectedSlot: null, slots: [] });
      await loadSlotsForRow(appt, {
        warehouseRef: row.warehouseRef,
        dockId: row.dockId,
        date: row.date,
      });
    } catch (err) {
      // Notification handled by parent
      await loadSlotsForRow(appt);
    } finally {
      setReschedulingId(null);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center px-fluid-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-6xl p-fluid-4 space-y-fluid-4"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        <div className="flex items-center gap-fluid-2">
          <div className="bg-[#fef3c7] text-[#92400e] rounded-full p-2">
            <AlertTriangle size={20} />
          </div>
          <div>
            <h2 className="text-fluid-xl font-semibold text-[#111827]">
              {title}
            </h2>
            <p className="text-sm text-gray-600">
              Review the appointments that will be affected before closing the
              dock(s).
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="py-fluid-4">
            <Loader message="Loading appointments..." />
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-700">
              {appointments.length > 0
                ? `${appointments.length} appointment(s) found in the selected range.`
                : "No appointments found in the selected range."}
            </div>

            <div className="w-full overflow-x-auto overflow-y-auto max-h-[420px] rounded-lg border border-gray-200">
              <table className="min-w-full table-fixed border-collapse bg-white">
                <colgroup>
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <thead>
                  <tr className="bg-[#223688] text-white">
                    {["Dock", "Shipment", "Carrier", "Start", "End", ""].map(
                      (header) => (
                        <th
                          key={header}
                          className="px-fluid-3 py-fluid-2 text-left font-semibold text-fluid-sm border-b border-gray-200"
                        >
                          {header}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {appointments.length > 0 ? (
                    appointments.map((appt) => {
                      const id = appt.appointmentId;
                      const row = rowStateById[id] || buildRowState(appt);
                      return (
                        <React.Fragment key={id}>
                          <tr className="hover:bg-gray-50 border-b border-gray-100">
                            <td className="px-fluid-3 py-fluid-2 text-sm text-gray-900">
                              {appt.dockName || appt.dockNumber || "-"}
                            </td>
                            <td className="px-fluid-3 py-fluid-2 text-sm text-gray-900">
                              {appt.shipmentNumber || "-"}
                            </td>
                            <td className="px-fluid-3 py-fluid-2 text-sm text-gray-900">
                              {appt.carrierName || "-"}
                            </td>
                            <td className="px-fluid-3 py-fluid-2 text-sm text-gray-900">
                              {formatDateTime(appt.startTime)}
                            </td>
                            <td className="px-fluid-3 py-fluid-2 text-sm text-gray-900">
                              {formatDateTime(appt.endTime)}
                            </td>
                            <td className="px-fluid-3 py-fluid-2 text-sm text-gray-900">
                              <button
                                onClick={() => handleToggleRow(appt)}
                                className="px-2 py-1 rounded-md border border-[#1f2d6b] text-[#1f2d6b] text-xs hover:bg-[#f4f5ff]"
                              >
                                {row.expanded ? "Hide" : "Reschedule"}
                              </button>
                            </td>
                          </tr>
                          {row.expanded && (
                            <tr className="bg-gray-50">
                              <td colSpan={6} className="px-fluid-3 py-fluid-3">
                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-fluid-2 items-end">
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                      Warehouse
                                    </label>
                                    <select
                                      value={row.warehouseRef}
                                      onChange={(event) =>
                                        handleWarehouseChange(appt, event.target.value)
                                      }
                                      className="border border-gray-300 rounded-md px-2 py-1.5 w-full text-sm focus:border-[#1e2a74] focus:outline-none"
                                    >
                                      <option value="">Select warehouse</option>
                                      {warehouses.map((wh) => (
                                        <option key={wh.locationRef} value={wh.locationRef}>
                                          {wh.locationName}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                      Dock
                                    </label>
                                    {row.isDocksLoading ? (
                                      <Loader message="Loading docks..." compact />
                                    ) : (
                                      <select
                                        value={row.dockId}
                                        onChange={(event) =>
                                          handleDockChange(appt, event.target.value)
                                        }
                                        className="border border-gray-300 rounded-md px-2 py-1.5 w-full text-sm focus:border-[#1e2a74] focus:outline-none"
                                      >
                                        <option value="">Select dock</option>
                                        {row.docks.map((dock) => (
                                          <option
                                            key={dock.dockId || dock.dockNumber}
                                            value={String(dock.dockId || dock.dockNumber)}
                                          >
                                            {dock.dockName || `Dock ${dock.dockNumber || dock.dockId}`}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                    {row.dockError && (
                                      <div className="text-xs text-red-600 mt-1">
                                        {row.dockError}
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                      Date
                                    </label>
                                    <input
                                      type="date"
                                      value={row.date}
                                      lang="en-US"
                                      onChange={(event) =>
                                        handleDateChange(appt, event.target.value)
                                      }
                                      onClick={(event) => {
                                        if (event.target.showPicker) {
                                          try {
                                            event.target.showPicker();
                                          } catch (err) {
                                            // Ignore gesture-related picker errors.
                                          }
                                        }
                                      }}
                                      className="border border-gray-300 rounded-md px-2 py-1.5 w-full text-sm focus:border-[#1e2a74] focus:outline-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                      Slot
                                    </label>
                                    {row.isSlotsLoading ? (
                                      <Loader message="Loading slots..." compact />
                                    ) : (
                                      <select
                                        value={row.selectedSlot?.startTime || ""}
                                        onChange={(event) => {
                                          const slot = row.slots.find(
                                            (s) => s.startTime === event.target.value
                                          );
                                          handleSlotChange(id, slot || null);
                                        }}
                                        className="border border-gray-300 rounded-md px-2 py-1.5 w-full text-sm focus:border-[#1e2a74] focus:outline-none"
                                      >
                                        <option value="">Select slot</option>
                                        {row.slots.length === 0 && (
                                          <option value="" disabled>
                                            No slots available
                                          </option>
                                        )}
                                        {row.slots.map((slot) => (
                                          <option key={slot.startTime} value={slot.startTime}>
                                            {slot.startTime}
                                          </option>
                                        ))}
                                      </select>
                                    )}
                                    {row.slotError && (
                                      <div className="text-xs text-red-600 mt-1">
                                        {row.slotError}
                                      </div>
                                    )}
                                    {!row.slotError && !row.isSlotsLoading && row.slots.length === 0 && (
                                      <div className="text-xs text-gray-600 mt-1">
                                        No slots available for this dock/date.
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center justify-end mt-fluid-2">
                                  <button
                                    onClick={() => handleReschedule(appt)}
                                    disabled={
                                      !row.selectedSlot ||
                                      !row.warehouseRef ||
                                      !row.dockId ||
                                      !row.date ||
                                      reschedulingId === id
                                    }
                                    className="px-fluid-button-x py-fluid-button-y rounded-full bg-[#223688] text-white hover:opacity-90 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {reschedulingId === id ? "Updating..." : "Update"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-fluid-3 py-fluid-4 text-center text-emerald-700 text-fluid-sm font-semibold bg-emerald-50"
                      >
                        <span className="inline-flex items-center gap-2">
                          {/* check-circle icon */}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-4 h-4 text-emerald-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                          </svg>
                          All appointments handled. Safe to close docks.
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-fluid-2 pt-fluid-2">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-fluid-button-x py-fluid-button-y rounded-full border border-[#1f2d6b] text-[#1f2d6b] hover:bg-[#f4f5ff] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onProceed}
            disabled={isLoading || isSubmitting}
            className="px-fluid-button-x py-fluid-button-y rounded-full bg-[#c63b2d] text-white hover:opacity-90 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Closing..." : "Proceed to Close"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ClosureWarningModal;
